import { cn } from "@/lib/utils";
import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area";
import { cva } from "class-variance-authority";
import { forwardRef } from "react";
import { DecorateInteraction } from "./decorate/interaction/decorate-interaction";

// ============================================
// 타입 정의
// ============================================

type ScrollbarSize = "normal" | "small";

type InteractionVariant = "light" | "normal" | "strong";
type InteractionColor = "neutral" | "blue" | "white";

interface ScrollbarAreaProps
  extends React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Root> {
  size?: ScrollbarSize;
  /** DecorateInteraction variant (default: "normal") */
  interactionVariant?: InteractionVariant;
  /** DecorateInteraction color (default: "neutral") */
  interactionColor?: InteractionColor;
  /**
   * Viewport(스크롤 되는 영역)에 얹을 클래스.
   *
   * Radix 는 Viewport 안에 `display: table` wrapper 를 하나 끼워 넣는다. table 은 콘텐츠
   * 폭만큼 늘어나므로 자식의 `w-full`·`flex-1` 이 viewport 폭이 아니라 콘텐츠 폭을
   * 기준으로 잡히고, 그 결과 `truncate` 가 동작하지 않고 가로 스크롤이 생긴다.
   * 세로 스크롤만 필요한 목록에서는 `[&>div]:!block` 로 wrapper 를 block 으로 되돌린다.
   */
  viewportClassName?: string;
  /** 실제로 스크롤 되는 Viewport 엘리먼트 ref. scrollTop 을 직접 다뤄야 할 때 쓴다. */
  viewportRef?: React.Ref<HTMLDivElement>;
}

const scrollbarContainerVariants = cva(
  "flex touch-none select-none p-[0.1875rem]",
  {
    variants: {
      size: {
        normal: "",
        small: "",
      },
      orientation: {
        vertical: "",
        horizontal: "flex-col",
      },
    },
    compoundVariants: [
      {
        size: "normal",
        orientation: "vertical",
        className: "w-[0.8125rem]",
      },
      {
        size: "normal",
        orientation: "horizontal",
        className: "h-[0.8125rem]",
      },
      {
        size: "small",
        orientation: "vertical",
        className: "w-[0.5625rem]",
      },
      {
        size: "small",
        orientation: "horizontal",
        className: "h-[0.5625rem]",
      },
    ],
  },
);

// ============================================
// ScrollbarArea 컴포넌트
// ============================================

const ScrollbarArea = forwardRef<
  React.ComponentRef<typeof ScrollAreaPrimitive.Root>,
  ScrollbarAreaProps
>(
  (
    {
      children,
      className,
      size = "normal",
      interactionVariant = "normal",
      interactionColor = "neutral",
      viewportClassName,
      viewportRef,
      ...props
    },
    ref,
  ) => (
    <ScrollAreaPrimitive.Root
      ref={ref}
      className={cn("relative overflow-hidden", className)}
      {...props}
    >
      <ScrollAreaPrimitive.Viewport
        ref={viewportRef}
        className={cn("h-full w-full rounded-[inherit]", viewportClassName)}
      >
        {children}
      </ScrollAreaPrimitive.Viewport>
      <ScrollBar
        orientation="vertical"
        size={size}
        interactionVariant={interactionVariant}
        interactionColor={interactionColor}
      />
      <ScrollBar
        orientation="horizontal"
        size={size}
        interactionVariant={interactionVariant}
        interactionColor={interactionColor}
      />
      <ScrollAreaPrimitive.Corner />
    </ScrollAreaPrimitive.Root>
  ),
);

// ============================================
// ScrollBar (thumb + track)
// ============================================

const ScrollBar = forwardRef<
  React.ComponentRef<typeof ScrollAreaPrimitive.Scrollbar>,
  React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Scrollbar> & {
    size: ScrollbarSize;
    interactionVariant?: InteractionVariant;
    interactionColor?: InteractionColor;
  }
>(
  (
    {
      className,
      orientation = "vertical",
      size,
      interactionVariant = "normal",
      interactionColor = "neutral",
      ...props
    },
    ref,
  ) => {
    return (
      <ScrollAreaPrimitive.Scrollbar
        ref={ref}
        orientation={orientation}
        className={cn(
          scrollbarContainerVariants({ size, orientation }),
          className,
        )}
        {...props}
      >
        <DecorateInteraction
          asChild
          variant={interactionVariant}
          color={interactionColor}
        >
          <ScrollAreaPrimitive.Thumb className="flex-1 rounded-full bg-[#171717]/24" />
        </DecorateInteraction>
      </ScrollAreaPrimitive.Scrollbar>
    );
  },
);

ScrollbarArea.displayName = "ScrollbarArea";
ScrollBar.displayName = "ScrollBar";

export { ScrollBar, ScrollbarArea };
export default ScrollbarArea;
